/**
 * Social Needs Screener — shared types + thin API client.
 *
 * Base path: /api/social-needs-screener (all authenticated).
 * IMPORTANT: every call goes through authFetch so the X-CBO-ID header is sent
 * (see lib/authFetch.ts). Never use raw fetch here.
 *
 * Ships the AHC-HRSN core (CMS, public domain) — the no-cost alternative to the
 * licensed PRAPARE. Scoring is categorical: each domain is flagged as a positive
 * (unmet) need or not; interpersonal safety uses a summed HITS score.
 */

import { authFetchJson } from '@/lib/authFetch';

const BASE = '/api/social-needs-screener';

export type ResponseType = 'single' | 'multi' | 'scale';

export type AdministrationStatus =
  | 'in_progress'
  | 'submitted'
  | 'scored'
  | 'reviewed'
  | string;

// ── Instrument catalog ───────────────────────────────────────────
export interface InstrumentSummary {
  code: string;
  name: string;
  description: string;
  version: number;
  total_items: number;
  is_system: boolean;
  is_licensed: boolean;
  status: string;
}

export interface ScreenerOption {
  code: string; // this is what raw_response uses
  label: string;
  value: number;
  triggers_need: boolean;
}

export interface ScreenerDomain {
  code: string;
  name: string;
  description: string | null;
  is_safety_critical: boolean;
  display_order: number;
}

export interface ScreenerItem {
  item_number: number;
  prompt: string;
  help_text: string | null;
  response_type: ResponseType;
  response_options: ScreenerOption[];
  domain_code: string;
  domain_name: string;
}

export interface InstrumentDetail {
  instrument: {
    code: string;
    name: string;
    description: string;
    version: number;
    total_items: number;
  };
  domains: ScreenerDomain[];
  items: ScreenerItem[];
}

// ── Administrations ──────────────────────────────────────────────
export interface CreateAdministrationResult {
  administration_id: string;
  public_token: string;
  status: AdministrationStatus;
}

export interface DomainResult {
  domain_code: string;
  domain_name?: string;
  positive: boolean;
  raw_score: number | null;
  detail: string;
  is_safety_critical?: boolean;
}

export interface MemberAdministration {
  id: string;
  member_id: string | null;
  status: AdministrationStatus;
  is_simulation: boolean;
  positive_domain_count: number;
  started_at: string | null;
  submitted_at: string | null;
  scored_at: string | null;
  instrument_code: string;
  instrument_name: string;
  first_name: string | null;
  last_name: string | null;
  open_safety_events: number;
}

export interface ListAdministrationsResult {
  administrations: MemberAdministration[];
}

export interface SaveResponsesResult {
  saved: number;
  safety_triggered: boolean;
}

/** A referral auto-created by the clinical<->social handoff from a positive screen. */
export interface CreatedReferral {
  domain: string;
  created: boolean;
  referral_id?: number;
  referral_number?: string;
  service_type?: string;
  reason?: string;
}

export interface SubmitResult {
  administration_id: string;
  status: AdministrationStatus;
  positive_domain_count: number;
  safety_triggered: boolean;
  domains: DomainResult[];
  /** Present when the clinical<->social handoff is enabled; auto-created referrals. */
  referrals_created?: CreatedReferral[];
}

export interface ResultsResult {
  administration_id: string;
  status: AdministrationStatus;
  is_simulation: boolean;
  domains: DomainResult[];
}

export interface SimulateResult {
  instrument_code: string;
  is_simulation: boolean;
  positive_domain_count: number;
  safety_triggered: boolean;
  domains: DomainResult[];
}

export interface SafetyEvent {
  id: string;
  administration_id: string;
  member_id: string | null;
  domain_code: string;
  severity: string;
  detail: string;
  status: string;
  triggered_at: string;
  first_name: string | null;
  last_name: string | null;
}

// ── Response payloads sent to the backend ────────────────────────
export interface ItemResponse {
  item_number: number;
  // single -> "often"; multi -> ["pests","mold"]; scale -> "3"
  raw_response: string | string[] | null;
}

// ── Safety-net resources (shown when interpersonal safety triggers) ──
export const SAFETY_RESOURCES = [
  {
    name: 'National DV Hotline',
    detail: 'Call 1-800-799-7233 or text START to 88788 (24/7, free, confidential).',
    href: 'tel:18007997233',
    action: 'Call 1-800-799-7233',
  },
  {
    name: '988 Suicide & Crisis Lifeline',
    detail: 'Call or text 988 (24/7, free, confidential).',
    href: 'tel:988',
    action: 'Call or text 988',
  },
  {
    name: 'Emergency',
    detail: 'If you are in immediate danger, call 911.',
    href: 'tel:911',
    action: 'Call 911',
  },
] as const;

// ── API client ───────────────────────────────────────────────────
export const socialNeedsScreenerApi = {
  listInstruments(): Promise<{ instruments: InstrumentSummary[] }> {
    return authFetchJson(`${BASE}/instruments`);
  },

  getInstrument(code: string): Promise<InstrumentDetail> {
    return authFetchJson(`${BASE}/instruments/${encodeURIComponent(code)}`);
  },

  createAdministration(body: {
    instrument_code: string;
    member_id?: string | null;
    is_simulation?: boolean;
  }): Promise<CreateAdministrationResult> {
    return authFetchJson(`${BASE}/administrations`, { method: 'POST', jsonBody: body });
  },

  listAdministrations(
    memberId: string | number,
    opts: { status?: string; includeSimulations?: boolean } = {},
  ): Promise<ListAdministrationsResult> {
    const params = new URLSearchParams({ member_id: String(memberId) });
    if (opts.status) params.set('status', opts.status);
    if (opts.includeSimulations) params.set('include_simulations', 'true');
    return authFetchJson(`${BASE}/administrations?${params.toString()}`);
  },

  saveResponses(
    administrationId: string,
    body: { responses: ItemResponse[]; completion_time_seconds?: number },
  ): Promise<SaveResponsesResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/responses`,
      { method: 'PUT', jsonBody: body },
    );
  },

  submit(administrationId: string): Promise<SubmitResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/submit`,
      { method: 'POST' },
    );
  },

  getResults(administrationId: string): Promise<ResultsResult> {
    return authFetchJson(
      `${BASE}/administrations/${encodeURIComponent(administrationId)}/results`,
    );
  },

  simulate(body: {
    instrument_code: string;
    positive_domains: string[];
    seed?: number;
  }): Promise<SimulateResult> {
    return authFetchJson(`${BASE}/administrations/simulate`, { method: 'POST', jsonBody: body });
  },

  listSafetyEvents(status: string = 'open'): Promise<{ safety_events: SafetyEvent[] }> {
    return authFetchJson(`${BASE}/safety-events?status=${encodeURIComponent(status)}`);
  },

  acknowledgeSafetyEvent(eventId: string): Promise<{ acknowledged: boolean; event_id: string }> {
    return authFetchJson(
      `${BASE}/safety-events/${encodeURIComponent(eventId)}/acknowledge`,
      { method: 'POST' },
    );
  },
};
